/* Created by Interactive 63 - https://interactive63.com */
/**
 * Makes two or more shapes compatible for morphing: the same number of subpaths, the same
 * number of segments in each, and start points that line up. All of it works on cubic subpaths
 * and is pure, so it can be tested without a browser.
 */
import type { Cubic, Subpath } from './normalise';

export interface Alignment {
  shift: number;
  reverse: boolean;
}

export interface MatchOptions {
  /** Whether reversing a single subpath is allowed. Multi-part shapes only reverse with evenodd. */
  allowReverse: boolean;
}

const COARSE_LIMIT = 400;

/** The bounding box of a subpath's points, control points included. */
function bounds(path: Subpath): { x0: number; y0: number; x1: number; y1: number } {
  let x0 = path.start[0];
  let y0 = path.start[1];
  let x1 = x0;
  let y1 = y0;
  for (const seg of path.segs) {
    for (let i = 0; i < 6; i += 2) {
      x0 = Math.min(x0, seg[i] as number);
      x1 = Math.max(x1, seg[i] as number);
      y0 = Math.min(y0, seg[i + 1] as number);
      y1 = Math.max(y1, seg[i + 1] as number);
    }
  }
  return { x0, y0, x1, y1 };
}

/** The area of a subpath's bounding box. */
function area(path: Subpath): number {
  const b = bounds(path);
  return (b.x1 - b.x0) * (b.y1 - b.y0);
}

/** The centre of the bounding box around every subpath of a frame. */
function centreOf(frame: readonly Subpath[]): [number, number] {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const path of frame) {
    const b = bounds(path);
    x0 = Math.min(x0, b.x0);
    y0 = Math.min(y0, b.y0);
    x1 = Math.max(x1, b.x1);
    y1 = Math.max(y1, b.y1);
  }
  return [(x0 + x1) / 2, (y0 + y1) / 2];
}

/** A shape's parts, largest first: the order morphs pair parts in, and that pins count points in. */
export function largestFirst(frame: readonly Subpath[]): Subpath[] {
  return [...frame].sort((a, b) => area(b) - area(a));
}

/** A subpath that is a single point, used to pad shapes with fewer parts. */
function degenerate(at: [number, number], closed: boolean): Subpath {
  const [x, y] = at;
  return { start: [x, y], segs: [[x, y, x, y, x, y]], closed };
}

/**
 * Gives every frame the same number of subpaths. Subpaths are ordered largest first so outlines
 * pair with outlines and holes with holes; shorter frames gain single-point subpaths at their
 * centre, which grow into place. A subpath is closed only if it is closed in every frame.
 */
export function matchSubpaths(frames: readonly (readonly Subpath[])[]): Subpath[][] {
  const sorted = frames.map(largestFirst);
  const count = Math.max(0, ...sorted.map((frame) => frame.length));
  const padded = sorted.map((frame) => {
    const centre = centreOf(frame);
    const result = [...frame];
    while (result.length < count) result.push(degenerate(centre, true));
    return result;
  });
  return padded.map((frame) =>
    frame.map((path, i) => {
      const closed = padded.every((other) => other[i]?.closed ?? true);
      return closed === path.closed ? path : { ...path, closed };
    }),
  );
}

/** Splits a cubic at t = 0.5 with de Casteljau's algorithm. */
function split(start: [number, number], seg: Cubic): [Cubic, Cubic] {
  const [x0, y0] = start;
  const [c1x, c1y, c2x, c2y, x3, y3] = seg;
  const ax = (x0 + c1x) / 2;
  const ay = (y0 + c1y) / 2;
  const bx = (c1x + c2x) / 2;
  const by = (c1y + c2y) / 2;
  const cx = (c2x + x3) / 2;
  const cy = (c2y + y3) / 2;
  const dx = (ax + bx) / 2;
  const dy = (ay + by) / 2;
  const ex = (bx + cx) / 2;
  const ey = (by + cy) / 2;
  const mx = (dx + ex) / 2;
  const my = (dy + ey) / 2;
  return [
    [ax, ay, dx, dy, mx, my],
    [ex, ey, cx, cy, x3, y3],
  ];
}

/** An estimate of a segment's length: its chord plus its control polygon, halved. */
function lengthOf(start: [number, number], seg: Cubic): number {
  const [x0, y0] = start;
  const chord = Math.hypot(seg[4] - x0, seg[5] - y0);
  const poly =
    Math.hypot(seg[0] - x0, seg[1] - y0) +
    Math.hypot(seg[2] - seg[0], seg[3] - seg[1]) +
    Math.hypot(seg[4] - seg[2], seg[5] - seg[3]);
  return (chord + poly) / 2;
}

/** Adds segments by repeatedly splitting the longest one until there are `target`. */
export function subdivide(path: Subpath, target: number): Subpath {
  const segs = [...path.segs];
  while (segs.length < target) {
    let longest = 0;
    let best = -1;
    for (let i = 0; i < segs.length; i += 1) {
      const from: [number, number] =
        i === 0 ? path.start : [segs[i - 1]?.[4] ?? 0, segs[i - 1]?.[5] ?? 0];
      const length = lengthOf(from, segs[i] as Cubic);
      if (length > best) {
        best = length;
        longest = i;
      }
    }
    const from: [number, number] =
      longest === 0 ? path.start : [segs[longest - 1]?.[4] ?? 0, segs[longest - 1]?.[5] ?? 0];
    const [a, b] = split(from, segs[longest] as Cubic);
    segs.splice(longest, 1, a, b);
  }
  return { ...path, segs };
}

/** Gives every frame the same number of segments in each subpath. */
export function equaliseSegments(frames: readonly (readonly Subpath[])[]): Subpath[][] {
  const count = frames[0]?.length ?? 0;
  const targets = Array.from({ length: count }, (_, i) =>
    Math.max(...frames.map((frame) => frame[i]?.segs.length ?? 0)),
  );
  return frames.map((frame) =>
    frame.map((path, i) => subdivide(path, targets[i] ?? path.segs.length)),
  );
}

/** The on-curve points of a subpath: its start, then the end of each segment. */
function onCurve(path: Subpath): [number, number][] {
  return [path.start, ...path.segs.map((s): [number, number] => [s[4], s[5]])];
}

/** Rotates a closed subpath so a different segment comes first. */
export function rotateSubpath(path: Subpath, shift: number): Subpath {
  const n = path.segs.length;
  const r = ((shift % n) + n) % n;
  if (r === 0 || !path.closed) return path;
  const previous = path.segs[r - 1] as Cubic;
  return {
    start: [previous[4], previous[5]],
    segs: [...path.segs.slice(r), ...path.segs.slice(0, r)],
    closed: true,
  };
}

/** Reverses the direction of a subpath, keeping its shape. */
export function reverseSubpath(path: Subpath): Subpath {
  const segs: Cubic[] = [];
  for (let i = path.segs.length - 1; i >= 0; i -= 1) {
    const seg = path.segs[i] as Cubic;
    const from: [number, number] =
      i === 0 ? path.start : [(path.segs[i - 1] as Cubic)[4], (path.segs[i - 1] as Cubic)[5]];
    segs.push([seg[2], seg[3], seg[0], seg[1], from[0], from[1]]);
  }
  const last = path.segs[path.segs.length - 1] as Cubic;
  return { start: [last[4], last[5]], segs, closed: path.closed };
}

/** Sum of squared distances between corresponding on-curve points. */
function score(a: Subpath, b: Subpath): number {
  const pa = onCurve(a);
  const pb = onCurve(b);
  let total = 0;
  for (let i = 0; i < pa.length; i += 1) {
    const [ax, ay] = pa[i] as [number, number];
    const [bx, by] = pb[i] as [number, number];
    total += (ax - bx) ** 2 + (ay - by) ** 2;
  }
  return total;
}

/** The rotation of a candidate that best matches a target, searching coarsely when it is long. */
function bestShift(target: Subpath, candidate: Subpath): { shift: number; value: number } {
  const n = candidate.segs.length;
  if (!candidate.closed || n === 1) return { shift: 0, value: score(target, candidate) };
  const step = Math.max(1, Math.ceil(n / COARSE_LIMIT));
  let best = { shift: 0, value: Infinity };
  for (let r = 0; r < n; r += step) {
    const value = score(target, rotateSubpath(candidate, r));
    if (value < best.value) best = { shift: r, value };
  }
  if (step > 1) {
    for (let r = best.shift - step; r <= best.shift + step; r += 1) {
      const wrapped = ((r % n) + n) % n;
      const value = score(target, rotateSubpath(candidate, wrapped));
      if (value < best.value) best = { shift: wrapped, value };
    }
  }
  return best;
}

/** Applies a stated alignment to a subpath. */
export function applyAlignment(path: Subpath, alignment: Alignment): Subpath {
  const base = alignment.reverse ? reverseSubpath(path) : path;
  return rotateSubpath(base, alignment.shift);
}

/**
 * Aligns a frame against the one before it. Each subpath tries every starting segment, and
 * the reversed direction where that is allowed, and keeps the closest. A manual alignment
 * replaces the search.
 */
export function alignFrame(
  previous: readonly Subpath[],
  frame: readonly Subpath[],
  options: MatchOptions,
  manual?: Alignment,
): Subpath[] {
  const mayReverse = options.allowReverse || frame.length === 1;
  return frame.map((path, i) => {
    if (manual) return applyAlignment(path, manual);
    const target = previous[i];
    if (!target) return path;
    const forward = bestShift(target, path);
    let best = { path: rotateSubpath(path, forward.shift), value: forward.value };
    if (mayReverse) {
      const reversed = reverseSubpath(path);
      const backward = bestShift(target, reversed);
      if (backward.value < best.value) {
        best = { path: rotateSubpath(reversed, backward.shift), value: backward.value };
      }
    }
    return best.path;
  });
}

/** Everything needed to make a list of frames morph-compatible. */
export function prepareFrames(
  frames: readonly (readonly Subpath[])[],
  options: MatchOptions,
  manual: readonly (Alignment | undefined)[] = [],
): Subpath[][] {
  const equal = equaliseSegments(matchSubpaths(frames));
  const result: Subpath[][] = [];
  equal.forEach((frame, index) => {
    const before = result[index - 1];
    result.push(before ? alignFrame(before, frame, options, manual[index]) : frame);
  });
  return result;
}
